import { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { SUBMISSION_STATUSES } from '@didaskill/domain/assignments/constants';
import { OVERALL_AVERAGE_GRADING_SCALE } from '@didaskill/domain/courses/constants';
import { getTranscriptAssignmentsCount } from '@didaskill/domain/students/utils';
import type { StudentAcademicTranscript } from '@didaskill/domain/users/types';
import { Typography } from '@didaskill/ui/design-system';
import { academicTranscriptMessages, globalMessages, gradeMessages } from '@didaskill/ui/intl';

import * as styles from './AcademicTranscriptFooter.styles';

export const AcademicTranscriptFooterBase = ({
  transcript,
}: {
  transcript: StudentAcademicTranscript;
}) => {
  const overallGradedAssignments = useMemo(
    () => getTranscriptAssignmentsCount(transcript, [SUBMISSION_STATUSES.CORRECTED]),
    [transcript],
  );

  return (
    <div css={styles.footer}>
      <div css={styles.overallAverage}>
        <Typography variant={Typography.VARIANTS.HEADING_3} element="span">
          <FormattedMessage {...academicTranscriptMessages.OVERALL_AVERAGE} />
        </Typography>

        <Typography
          variant={Typography.VARIANTS.HEADING_5}
          element="span"
          css={styles.coloredTextPrimary}
        >
          <FormattedMessage
            {...(Number.isFinite(transcript.overallAverage)
              ? gradeMessages.GRADE
              : globalMessages.NOT_AFFECTED)}
            values={{
              grade: transcript.overallAverage,
              gradingScale: OVERALL_AVERAGE_GRADING_SCALE,
              Grade: (chunks) => (
                <Typography variant={Typography.VARIANTS.HEADING_3} element="span">
                  {chunks}
                </Typography>
              ),
              GradingScale: (chunks) => chunks,
            }}
          />
        </Typography>
      </div>

      <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
        <FormattedMessage
          {...gradeMessages.GRADED_ASSIGNMENTS_COUNT}
          values={{
            ...overallGradedAssignments,
            b: (chunks) => (
              <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>{chunks}</Typography>
            ),
          }}
        />
      </Typography>
    </div>
  );
};

AcademicTranscriptFooterBase.displayName = 'AcademicTranscriptFooter';

export const AcademicTranscriptFooter = memo(AcademicTranscriptFooterBase);
